import { useMemo, useState, type FormEvent } from 'react';
import {
  ALL_DAYS_MASK,
  habitStripDates,
  isoDateParts,
  weekdayOfIsoDate,
  type Habit,
  type HabitGoalType,
} from '@cleartask/shared';
import type { UpdateHabitPayload } from '@cleartask/shared';
import { ColorField } from '../../components/ui/ColorField.js';
import { IconField } from '../../components/ui/IconField.js';
import { useToast } from '../../components/ui/Toast.js';
import { HabitRow } from './HabitRow.js';
import {
  newHabitId,
  useCreateHabit,
  useHabitStats,
  useHabits,
  useLogHabit,
  useUpdateHabit,
} from './useHabits.js';

/**
 * O mesmo padrao do DDL (`migrations/0001_init.sql`).
 *
 * Repetido aqui de proposito, e nao lido de `shared`: o formulario precisa de
 * um valor para o campo de cor JA no primeiro render, antes de qualquer
 * requisicao. O que o DDL garante e o padrao de quem cria sem passar cor — e
 * agora ninguem cria sem passar.
 */
const COR_PADRAO_HABITO = '#07B494';

/** Iniciais dos dias, domingo primeiro — a mesma ordem do `days_mask`. */
const DIAS_PT = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;
const DIAS_NOME_PT = [
  'domingo',
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
] as const;

/**
 * Lista de habitos (spec 9.5).
 *
 * O "hoje" vem do servidor (`stats.today`), nao do relogio do dispositivo.
 * Derivar aqui faria alguem em outro fuso marcar o habito no dia errado e ver a
 * faixa discordar do streak que o servidor devolve na mesma resposta.
 */
export function HabitList() {
  const { data: lista, isPending: carregandoLista } = useHabits();
  const { data: stats, isPending: carregandoStats } = useHabitStats();

  const registrar = useLogHabit();
  const atualizar = useUpdateHabit();
  const { showToast } = useToast();

  /** Qual habito esta com o formulario aberto — um por vez. */
  const [emEdicao, setEmEdicao] = useState<string | null>(null);

  const habitos = lista?.habits ?? [];

  const dates = useMemo(() => (stats ? habitStripDates(stats.today) : []), [stats]);

  /** Valor registrado, por habito e por data. */
  const valoresPorHabito = useMemo(() => {
    const mapa = new Map<string, Map<string, number>>();
    for (const log of stats?.logs ?? []) {
      const doHabito = mapa.get(log.habitId) ?? new Map<string, number>();
      doHabito.set(log.logDate, log.value);
      mapa.set(log.habitId, doHabito);
    }
    return mapa;
  }, [stats]);

  const statsPorHabito = useMemo(
    () => new Map((stats?.stats ?? []).map((s) => [s.habitId, s])),
    [stats],
  );

  function arquivar(habit: Habit) {
    atualizar.mutate({ id: habit.id, patch: { archived: true } });

    // Desfazer, nao confirmar (spec 10.3): a volta e a mutacao inversa.
    showToast({
      message: `“${habit.name}” arquivado.`,
      action: {
        label: 'Desfazer',
        onAction: () => atualizar.mutate({ id: habit.id, patch: { archived: false } }),
      },
    });
  }

  if (carregandoLista || carregandoStats) {
    return <p className="p-4 text-sm text-text-mut">Carregando hábitos…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {stats && habitos.length > 0 ? <FaixaDeDias dates={dates} today={stats.today} /> : null}

      {habitos.length === 0 ? (
        <EstadoVazio />
      ) : (
        <ul className="flex flex-col gap-2">
          {habitos.map((habit) =>
            // O formulario ocupa o LUGAR da linha, em vez de abrir uma janela:
            // editar um habito e mexer em quatro campos curtos, e um modal por
            // cima da lista esconderia justamente os outros habitos, que sao a
            // referencia de quem esta ajustando uma meta.
            emEdicao === habit.id ? (
              <li key={habit.id}>
                <FormularioDeHabito
                  idBase={habit.id}
                  titulo={`Editar “${habit.name}”`}
                  inicial={{
                    name: habit.name,
                    daysMask: habit.daysMask,
                    goalType: habit.goalType,
                    goalTarget: habit.goalTarget,
                    unit: habit.unit,
                    icon: habit.icon,
                    color: habit.color,
                    reminderTime: habit.reminderTime,
                  }}
                  rotuloDoBotao="Salvar"
                  pendente={atualizar.isPending}
                  onCancel={() => setEmEdicao(null)}
                  onSubmit={(valores) => {
                    const patch = patchDeHabito(habit, valores);
                    // Salvar sem ter mudado nada nao vira requisicao — nem
                    // linha na auditoria dizendo que algo mudou.
                    if (Object.keys(patch).length > 0) {
                      atualizar.mutate({ id: habit.id, patch });
                    }
                    setEmEdicao(null);
                  }}
                />
              </li>
            ) : (
              <HabitRow
                key={habit.id}
                habit={habit}
                stats={statsPorHabito.get(habit.id)}
                dates={dates}
                values={valoresPorHabito.get(habit.id) ?? new Map()}
                today={stats?.today ?? ''}
                onLog={(value) => {
                  if (stats) registrar.mutate({ habit, date: stats.today, value });
                }}
                onEdit={() => setEmEdicao(habit.id)}
                onArchive={() => arquivar(habit)}
              />
            ),
          )}
        </ul>
      )}

      <NovoHabito />

      <HabitosArquivados />
    </div>
  );
}

/**
 * Os arquivados, com o caminho de volta.
 *
 * Arquivar existe para guardar o historico em vez de apaga-lo — mas, expirado o
 * "Desfazer" do toast, o habito sumia de todas as telas, e historico que
 * ninguem alcanca e historico apagado com passos extras.
 *
 * Fica recolhido e no fim da tela: e consulta rara, e aberto por padrao
 * competiria com os habitos de hoje. Sem arquivados, a secao nem aparece.
 *
 * Desarquivar devolve o habito a lista, onde a edicao ja existe; editar aqui
 * duplicaria o formulario para um habito que ninguem esta usando.
 */
function HabitosArquivados() {
  const { data } = useHabits(true);
  const atualizar = useUpdateHabit();

  const arquivados = (data?.habits ?? []).filter((h) => h.archived);
  if (arquivados.length === 0) return null;

  return (
    <details className="rounded bg-surface p-3 shadow-card">
      <summary className="min-h-touch flex cursor-pointer items-center text-sm text-text-mut">
        Arquivados ({arquivados.length})
      </summary>

      <ul className="mt-2 flex flex-col gap-1">
        {arquivados.map((habit) => (
          <li key={habit.id} className="flex items-center gap-3">
            {habit.icon ? <span aria-hidden="true">{habit.icon}</span> : null}
            <span className="min-w-0 flex-1 truncate">{habit.name}</span>
            <button
              type="button"
              aria-label={`Desarquivar “${habit.name}”`}
              disabled={atualizar.isPending}
              onClick={() => atualizar.mutate({ id: habit.id, patch: { archived: false } })}
              className="min-h-touch rounded-sm px-3 text-sm text-brand-blue hover:bg-surface-2 disabled:opacity-50"
            >
              Desarquivar
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * A faixa de datas do cabecalho, com hoje destacado.
 *
 * O wireframe pede circulo AMBAR. Aqui ele e azul: o ambar continua sendo a cor
 * semantica do quadrante II, e a propria spec 10.5 registra que ambar sobre
 * branco reprova em contraste AA. O azul-suave e justamente o token de "estado
 * selecionado".
 */
function FaixaDeDias({ dates, today }: { dates: string[]; today: string }) {
  return (
    <ol className="flex justify-between gap-1 rounded bg-surface px-2 py-2 shadow-card">
      {dates.map((date) => {
        const ehHoje = date === today;
        const weekday = weekdayOfIsoDate(date);

        return (
          <li key={date} className="flex flex-1 flex-col items-center gap-1">
            <span aria-hidden="true" className="text-xs text-text-mut">
              {DIAS_PT[weekday]}
            </span>

            <span
              className={[
                'flex h-8 w-8 items-center justify-center rounded-full font-mono text-sm',
                ehHoje ? 'bg-brand-blue-soft font-semibold text-brand-blue' : 'text-text-mut',
              ].join(' ')}
            >
              <span className="sr-only">
                {DIAS_NOME_PT[weekday]}
                {ehHoje ? ', hoje, ' : ', '}
              </span>
              {isoDateParts(date).day}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Estado vazio instrutivo (spec 9.5).
 *
 * A copy e obrigatoria: a spec proibe explicitamente a frase motivacional do
 * TickTick ("A perseveranca nos faz brilhar"). Vazio e convite a acao.
 */
function EstadoVazio() {
  return (
    <p className="rounded bg-surface p-4 text-center text-sm text-text-mut shadow-card">
      Nenhum hábito para hoje. Criar um hábito →
    </p>
  );
}

/** O que o formulario preenche — os mesmos campos na criacao e na edicao. */
interface ValoresDoHabito {
  name: string;
  daysMask: number;
  goalType: HabitGoalType;
  goalTarget: number;
  unit: string | null;
  icon: string | null;
  color: string;
  /** 'HH:MM' no fuso do usuario, ou null para nenhum aviso. */
  reminderTime: string | null;
}

const HABITO_EM_BRANCO: ValoresDoHabito = {
  name: '',
  daysMask: ALL_DAYS_MASK,
  goalType: 'boolean',
  goalTarget: 1,
  unit: null,
  icon: null,
  color: COR_PADRAO_HABITO,
  reminderTime: null,
};

/**
 * O formulario de habito, um so para criar e para editar.
 *
 * Dois formularios com os mesmos oito campos divergiriam na primeira regra que
 * alguem lembrasse de mexer num deles — e as regras aqui nao sao decorativas:
 * "alvo 1 quando e sim/nao", "unidade so na contagem", "dias como bitmask".
 *
 * `idBase` existe porque o `ColorField` precisa de um id unico no documento, e
 * na edicao pode haver um formulario aberto ao lado do de criacao.
 */
function FormularioDeHabito({
  idBase,
  titulo,
  inicial,
  rotuloDoBotao,
  pendente,
  onSubmit,
  onCancel,
}: {
  idBase: string;
  titulo: string;
  inicial: ValoresDoHabito;
  rotuloDoBotao: string;
  pendente: boolean;
  onSubmit: (valores: ValoresDoHabito) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(inicial.name);
  const [daysMask, setDaysMask] = useState<number>(inicial.daysMask);
  const [goalType, setGoalType] = useState<HabitGoalType>(inicial.goalType);
  // O alvo vive como texto enquanto esta na tela: `Number('')` e 0, e um campo
  // que a pessoa esta apagando para digitar outro numero nao pode passar por
  // zero.
  const [goalTarget, setGoalTarget] = useState(String(inicial.goalTarget));
  const [unit, setUnit] = useState(inicial.unit ?? '');
  const [icon, setIcon] = useState<string | null>(inicial.icon);
  const [color, setColor] = useState(inicial.color);
  // Texto vazio = sem lembrete: e o que o `<input type="time">` devolve quando
  // a pessoa apaga o campo, e evita um segundo estado so para "desligado".
  const [reminderTime, setReminderTime] = useState(inicial.reminderTime ?? '');

  const contagem = goalType === 'count';
  const alvo = Number(goalTarget);
  // O alvo so precisa ser valido quando ha alvo: um habito de sim/nao grava 1 e
  // o campo nem aparece.
  const alvoInvalido = contagem && (!Number.isFinite(alvo) || alvo <= 0);

  /**
   * Mudar meta ou dias previstos REESCREVE o passado.
   *
   * O streak nao e uma coluna: ele e recalculado a partir dos registros contra
   * a meta ATUAL (`computeHabitStreaks`). Baixar o alvo de 10 para 5 promove a
   * dias cumpridos varios dias que nao eram, e tirar a terça-feira dos dias
   * previstos deixa de cobrar um dia que quebrava a sequencia. Nada disso e
   * defeito — mas e surpresa, e surpresa em cima de historico e o tipo de
   * coisa que faz alguem desconfiar do numero para sempre.
   */
  const mexeNoHistorico =
    goalType !== inicial.goalType ||
    (contagem && alvo !== inicial.goalTarget) ||
    daysMask !== inicial.daysMask;

  function alternarDia(weekday: number) {
    setDaysMask((atual) => atual ^ (1 << weekday));
  }

  function submeter(e: FormEvent) {
    e.preventDefault();
    const nome = name.trim();
    if (!nome || alvoInvalido) return;

    onSubmit({
      name: nome,
      daysMask,
      goalType,
      // Sim/nao grava alvo 1, que e o que o calculo de streak espera de um
      // habito booleano (`packages/shared/src/habit.ts`): marcar o dia vale 1.
      goalTarget: contagem ? alvo : 1,
      // Unidade so faz sentido com contagem — "copos" de um habito de sim/nao
      // nao descreve nada, e ficaria escrito no card.
      unit: contagem && unit.trim() !== '' ? unit.trim() : null,
      icon,
      color,
      reminderTime: reminderTime === '' ? null : reminderTime,
    });
  }

  return (
    <form
      onSubmit={submeter}
      className="flex flex-col gap-3 rounded bg-surface p-3 shadow-card"
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-text-mut">{titulo}</span>
        <div className="flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Correr"
            className="min-h-touch flex-1 rounded-sm border border-border bg-bg px-3"
          />
          <ColorField
            id={`habit-color-${idBase}`}
            label="Cor do hábito"
            value={color}
            onChange={setColor}
          />
        </div>
      </label>

      <IconField label="Ícone" value={icon} onChange={setIcon} />

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-text-mut">Meta</span>
        <select
          value={goalType}
          onChange={(e) => setGoalType(e.target.value as HabitGoalType)}
          className="min-h-touch rounded-sm border border-border bg-bg px-3"
        >
          <option value="boolean">Fiz ou não fiz</option>
          <option value="count">Contagem por dia</option>
        </select>
      </label>

      {/*
        Alvo e unidade so aparecem na contagem. Um "quantos por dia" ao lado de
        um habito de sim/nao pediria um numero que nao muda nada — e campo que
        nao muda nada e campo que a pessoa preenche e depois nao entende.
      */}
      {contagem ? (
        <div className="flex gap-2">
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="text-text-mut">Quantos por dia</span>
            <input
              type="number"
              min={1}
              max={10000}
              value={goalTarget}
              onChange={(e) => setGoalTarget(e.target.value)}
              className="min-h-touch rounded-sm border border-border bg-bg px-3"
            />
          </label>

          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="text-text-mut">Unidade</span>
            <input
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              maxLength={20}
              placeholder="copos"
              className="min-h-touch rounded-sm border border-border bg-bg px-3"
            />
          </label>
        </div>
      ) : null}

      <fieldset className="flex flex-col gap-1">
        <legend className="text-sm text-text-mut">Dias da semana</legend>

        <div className="flex gap-1">
          {DIAS_NOME_PT.map((nome, weekday) => {
            const ativo = (daysMask & (1 << weekday)) !== 0;

            return (
              <button
                key={nome}
                type="button"
                aria-pressed={ativo}
                aria-label={nome}
                onClick={() => alternarDia(weekday)}
                className={[
                  'min-h-touch flex-1 rounded-sm text-sm transition-colors duration-150',
                  ativo
                    ? 'bg-brand-blue-soft font-medium text-brand-blue'
                    : 'bg-surface-2 text-text-mut',
                ].join(' ')}
              >
                <span aria-hidden="true">{DIAS_PT[weekday]}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/*
        A hora e de parede, no fuso do usuario: o servidor avisa nos dias
        marcados acima, so se o dia ainda nao foi cumprido, uma vez por dia.
      */}
      <div className="flex flex-col gap-1 text-sm">
        <label htmlFor={`habit-reminder-${idBase}`} className="text-text-mut">
          Lembrete
        </label>
        <div className="flex gap-2">
          <input
            id={`habit-reminder-${idBase}`}
            type="time"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
            className="min-h-touch min-w-0 flex-1 rounded-sm border border-border bg-bg px-3"
          />
          {reminderTime !== '' ? (
            <button
              type="button"
              onClick={() => setReminderTime('')}
              className="min-h-touch rounded-sm px-3 text-sm text-text-mut hover:bg-surface-2"
            >
              Sem lembrete
            </button>
          ) : null}
        </div>
      </div>

      {/*
        O aviso aparece ANTES de salvar, e nao num toast depois: depois o
        historico ja foi reavaliado, e a pessoa descobriria pelo numero mudado.
      */}
      {mexeNoHistorico ? (
        <p role="status" className="rounded-sm bg-surface-2 p-2 text-sm text-text-mut">
          Isto vale para o histórico inteiro: os dias já registrados passam a ser
          avaliados pela nova regra, e a sequência pode subir ou cair.
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={name.trim() === '' || alvoInvalido || pendente}
          className="min-h-touch flex-1 rounded-sm bg-brand-blue px-4 font-medium text-white disabled:opacity-50"
        >
          {rotuloDoBotao}
        </button>

        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="min-h-touch rounded-sm px-4 text-sm text-text-mut hover:bg-surface-2"
          >
            Cancelar
          </button>
        ) : null}
      </div>
    </form>
  );
}

/**
 * Criacao com tudo que o registro tem — sem ela, metade das colunas de `habits`
 * seria inalcancavel pela interface.
 *
 * `days_mask` ja estava aqui desde a fase 6. Meta, icone e cor entraram em
 * 12/08/2026: a API aceitava os tres (`createHabitSchema`), a leitura
 * funcionava — um habito de contagem criado via API ja renderizava campo
 * numerico com a unidade — e o formulario oferecia so nome e dias.
 */
function NovoHabito() {
  const criar = useCreateHabit();
  // Trocar a chave desmonta o formulario e o remonta com os valores em branco:
  // e como este formulario "limpa" depois de criar, sem um efeito de reset
  // campo a campo que esqueceria o proximo campo acrescentado.
  const [rodada, setRodada] = useState(0);

  return (
    <FormularioDeHabito
      key={rodada}
      idBase="novo"
      titulo="Novo hábito"
      inicial={HABITO_EM_BRANCO}
      rotuloDoBotao="Criar hábito"
      pendente={criar.isPending}
      onSubmit={(valores) => {
        criar.mutate({ id: newHabitId(), ...valores });
        setRodada((n) => n + 1);
      }}
    />
  );
}

/**
 * O que mudou, e so isso, vai no PATCH.
 *
 * Mandar o registro inteiro funcionaria — a API aceita —, mas a trilha de
 * auditoria guarda o `diff` recebido: um patch com os oito campos afirmaria
 * que oito campos mudaram toda vez que alguem corrigisse um acento no nome.
 */
function patchDeHabito(habit: Habit, valores: ValoresDoHabito): UpdateHabitPayload {
  const patch: UpdateHabitPayload = {};

  if (valores.name !== habit.name) patch.name = valores.name;
  if (valores.icon !== habit.icon) patch.icon = valores.icon;
  if (valores.color !== habit.color) patch.color = valores.color;
  if (valores.goalType !== habit.goalType) patch.goalType = valores.goalType;
  if (valores.goalTarget !== habit.goalTarget) patch.goalTarget = valores.goalTarget;
  if (valores.unit !== habit.unit) patch.unit = valores.unit;
  if (valores.daysMask !== habit.daysMask) patch.daysMask = valores.daysMask;
  if (valores.reminderTime !== habit.reminderTime) patch.reminderTime = valores.reminderTime;

  return patch;
}
